import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { VehicleLog, VehicleLogFilters, SortField, SortOrder } from "./types";
import { vehicleLogApi } from "./vehicle-log-api";
import { campusApi } from "../campus/campus-api";
import { Campus, Node } from "../campus/types";

const VehicleLogsSearch: React.FC = () => {
  const [logs, setLogs] = useState<VehicleLog[]>([]);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [count, setCount] = useState(0);

  const [filters, setFilters] = useState<VehicleLogFilters>({
    status: "all",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>("entry_time");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  useEffect(() => {
    fetchCampuses();
    fetchAllNodes();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, limit, filters, searchQuery, sortField, sortOrder]);

  const fetchCampuses = async () => {
    try {
      const data = await campusApi.getAllCampuses();
      setCampuses(data);
    } catch (error) {
      console.error("Failed to load campuses");
    }
  };

  const fetchAllNodes = async () => {
    try {
      const data = await campusApi.getAllNodes();
      setNodes(data);
    } catch (error) {
      console.error("Failed to load nodes");
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const filtersWithSearch: VehicleLogFilters = {
        ...filters,
        search: searchQuery || undefined,
      };
      const response = await vehicleLogApi.getVehicleLogs(
        filtersWithSearch,
        page,
        limit
      );

      if (response.status === "ok") {
        // Sort data client-side
        let sortedData = [...response.data];
        sortedData.sort((a, b) => {
          let aValue: any;
          let bValue: any;

          switch (sortField) {
            case "entry_time":
              aValue = a.entry_time ? new Date(a.entry_time).getTime() : 0;
              bValue = b.entry_time ? new Date(b.entry_time).getTime() : 0;
              break;
            case "exit_time":
              aValue = a.exit_time ? new Date(a.exit_time).getTime() : 0;
              bValue = b.exit_time ? new Date(b.exit_time).getTime() : 0;
              break;
            case "number_plate":
              aValue = (a.number_plate || a.vehicle?.reg_no || "").toLowerCase();
              bValue = (b.number_plate || b.vehicle?.reg_no || "").toLowerCase();
              break;
            case "status":
              aValue = a.status;
              bValue = b.status;
              break;
            case "make":
              aValue = (a.make || "").toLowerCase();
              bValue = (b.make || "").toLowerCase();
              break;
            case "vehicle_type":
              aValue = (a.vehicle_type || "").toLowerCase();
              bValue = (b.vehicle_type || "").toLowerCase();
              break;
            default:
              return 0;
          }

          if (typeof aValue === "string") {
            return sortOrder === "asc"
              ? aValue.localeCompare(bValue)
              : bValue.localeCompare(aValue);
          } else {
            return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
          }
        });

        setLogs(sortedData);
        setTotal(response.pagination.total);
        setTotalPages(response.pagination.total_pages);
        setCount(response.pagination.count);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to load vehicle logs");
      setLogs([]);
      setTotal(0);
      setTotalPages(0);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof VehicleLogFilters, value: any) => {
    setFilters({ ...filters, [key]: value });
    setPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({ status: "all" });
    setSearchQuery("");
    setPage(1);
    setSortField("entry_time");
    setSortOrder("desc");
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return (
      <i
        className={`fa-solid fa-sort-${sortOrder === "asc" ? "up" : "down"} ml-1 text-xs`}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Vehicle Logs Search
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Search, filter, and view vehicle entry and exit records
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Search (Plate Number)
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search by plate number..."
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={filters.status || "all"}
                onChange={(e) =>
                  handleFilterChange("status", e.target.value as "entry" | "exit" | "all")
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All</option>
                <option value="entry">Entry</option>
                <option value="exit">Exit</option>
              </select>
            </div>

            {/* Has Exit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Has Exit
              </label>
              <select
                value={
                  filters.has_exit === undefined
                    ? ""
                    : filters.has_exit
                    ? "true"
                    : "false"
                }
                onChange={(e) =>
                  handleFilterChange(
                    "has_exit",
                    e.target.value === "" ? undefined : e.target.value === "true"
                  )
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="true">Has Exit</option>
                <option value="false">No Exit</option>
              </select>
            </div>

            {/* Entry Campus */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Entry Campus
              </label>
              <select
                value={filters.entry_campus_id || filters.campus_id || ""}
                onChange={(e) =>
                  handleFilterChange(
                    "entry_campus_id",
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Campuses</option>
                {campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Entry Node */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Entry Node
              </label>
              <select
                value={filters.entry_node_id || ""}
                onChange={(e) =>
                  handleFilterChange("entry_node_id", e.target.value || undefined)
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Nodes</option>
                {nodes.map((node) => (
                  <option key={node.node_id} value={node.node_id}>
                    {node.node_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Exit Node */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Exit Node
              </label>
              <select
                value={filters.exit_node_id || ""}
                onChange={(e) =>
                  handleFilterChange("exit_node_id", e.target.value || undefined)
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Nodes</option>
                {nodes.map((node) => (
                  <option key={node.node_id} value={node.node_id}>
                    {node.node_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date
              </label>
              <input
                type="datetime-local"
                value={filters.startDate || filters.date_from || ""}
                onChange={(e) =>
                  handleFilterChange("startDate", e.target.value || undefined)
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date
              </label>
              <input
                type="datetime-local"
                value={filters.endDate || filters.date_to || ""}
                onChange={(e) =>
                  handleFilterChange("endDate", e.target.value || undefined)
                }
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              onClick={clearFilters}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Clear Filters
            </button>
            <button
              onClick={fetchLogs}
              className="btn-primary px-4 py-2"
              disabled={loading}
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Results Info and Sort */}
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {count} of {total} records (Page {page} of {totalPages})
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 dark:text-gray-300">Page Size:</label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                      onClick={() => handleSort("entry_time")}
                    >
                      Entry Time <SortIcon field="entry_time" />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                      onClick={() => handleSort("exit_time")}
                    >
                      Exit Time <SortIcon field="exit_time" />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                      onClick={() => handleSort("number_plate")}
                    >
                      Plate Number <SortIcon field="number_plate" />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Vehicle Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Entry Campus/Node/Camera
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Exit Campus/Node/Camera
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                      Owner/Member
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                      onClick={() => handleSort("status")}
                    >
                      Status <SortIcon field="status" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                        No logs found for the selected filters
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr
                        key={log.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {log.entry_time
                            ? new Date(log.entry_time).toLocaleString()
                            : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {log.exit_time ? new Date(log.exit_time).toLocaleString() : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {log.number_plate || log.vehicle?.reg_no}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          <div>
                            {log.vehicle_type && <div>{log.vehicle_type}</div>}
                            {log.make && <div className="text-xs">{log.make}</div>}
                            {log.primary_color && (
                              <div className="text-xs">
                                Color: {log.primary_color}
                                {log.secondary_colors && log.secondary_colors.length > 0 && (
                                  <span> ({log.secondary_colors.join(", ")})</span>
                                )}
                              </div>
                            )}
                            {log.confidence_score && (
                              <div className="text-xs text-gray-400">
                                Confidence: {log.confidence_score}%
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {log.entry_campus ? (
                            <div>
                              <div className="font-medium">{log.entry_campus.name}</div>
                              <div className="text-xs">{log.entry_campus.location}</div>
                              {log.entry_node_id && (
                                <div className="text-xs text-gray-400 mt-1">
                                  Node: {log.entry_node_id.substring(0, 8)}...
                                </div>
                              )}
                              {log.entry_camera_id && (
                                <div className="text-xs text-gray-400">
                                  Camera: {log.entry_camera_id}
                                </div>
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {log.exit_campus ? (
                            <div>
                              <div className="font-medium">{log.exit_campus.name}</div>
                              <div className="text-xs">{log.exit_campus.location}</div>
                              {log.exit_node_id && (
                                <div className="text-xs text-gray-400 mt-1">
                                  Node: {log.exit_node_id.substring(0, 8)}...
                                </div>
                              )}
                              {log.exit_camera_id && (
                                <div className="text-xs text-gray-400">
                                  Camera: {log.exit_camera_id}
                                </div>
                              )}
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {log.vehicle?.user ? (
                            <div>
                              <div className="font-medium">{log.vehicle.user.name}</div>
                              {log.vehicle.user.phone && (
                                <div className="text-xs">{log.vehicle.user.phone}</div>
                              )}
                              {log.vehicle.user.address && (
                                <div className="text-xs text-gray-400">
                                  {log.vehicle.user.address}
                                </div>
                              )}
                              <div className="text-xs mt-1">
                                {log.vehicle.visitor ? (
                                  <span className="text-orange-600">Visitor</span>
                                ) : (
                                  <span className="text-green-600">
                                    {log.vehicle.user.member_type || "Member"}
                                  </span>
                                )}
                                {log.vehicle.user.is_leader && (
                                  <span className="text-blue-600 ml-2">Leader</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.status === "entry"
                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                            }`}
                          >
                            {log.status === "entry" ? "Entry" : "Exit"}
                          </span>
                          {log.notes && (
                            <div className="text-xs text-gray-400 mt-1" title={log.notes}>
                              {log.notes.length > 30
                                ? log.notes.substring(0, 30) + "..."
                                : log.notes}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum:number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    disabled={loading}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      page === pageNum
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleLogsSearch;
